/* eslint-disable camelcase */
// Информация об адресе

import metro from "./metro";
import utils from "../utils";

const CAPITAL_MARKERS = {
    1: "районный центр",
    2: "центр региона",
    3: "центр района и региона",
    4: "центральный район региона",
};

const FIAS_LEVELS = {
    0: "страна",
    1: "регион",
    2: "автономный округ",
    3: "район",
    4: "город",
    5: "район города",
    6: "населенный пункт",
    7: "улица",
    8: "дом",
    9: "квартира",
    65: "планировочная структура",
    75: "земельный участок",
    90: "доп. территория",
    91: "улица в доп. территории",
};

const QC_GEO = {
    0: "точные",
    1: "ближайший дом",
    2: "улица",
    3: "населенный пункт",
    4: "город",
};

const QC_COMPLETE = {
    10: "Неизвестный дом",
    7: "", // иностранный
    5: "Еще можно указать квартиру",
    4: "Еще можно указать дом и квартиру",
    3: "Еще можно указать улицу, дом и квартиру",
    2: "Еще можно указать город, улицу, дом и квартиру",
    1: "Еще можно указать регион, город, улицу, дом и квартиру",
    0: "",
};

const QC_SEND = {
    0: "Подходит для рассылки",
    5: "Рассылка под вопросом: нет квартиры",
    7: "Не годится для рассылки: иностранный",
    8: "Рассылка под вопросом: до почтового отделения",
    9: "Рассылка под вопросом: неуверенный разбор",
    10: "Рассылка под вопросом: дома нет в ФИАС",
};

const BELTWAY = {
    IN_MKAD: "внутри МКАД",
    OUT_MKAD: "за МКАД",
    IN_KAD: "внутри КАД",
    OUT_KAD: "за КАД",
};

function beltwayName(beltway_hit, beltway_distance) {
    if (beltway_hit && beltway_distance) {
        return `${beltway_distance} км ${BELTWAY[beltway_hit] || beltway_hit}`;
    }
    return BELTWAY[beltway_hit] || beltway_hit;
}

function capitalMarkerName(capital_marker) {
    return CAPITAL_MARKERS[capital_marker] || capital_marker;
}

function fiasActualityStateName(val) {
    if (val === "0") return "актуальный";
    if (val === "51") return "переподчинен";
    if (val === "99") return "удален";

    const int_val = parseInt(val, 10);
    if (int_val && int_val > 0 && int_val < 51) return "переименован";

    return "";
}

function fiasLevelName(fias_level) {
    return FIAS_LEVELS[fias_level] || fias_level;
}

export function inferQcComplete(address) {
    if (address.fias_level === "-1") {
        // иностранный
        return 7;
    }
    if (!address.region) {
        // нет региона
        return 1;
    }
    if (!address.settlement && !address.city && address.region_type_full !== "город") {
        // нет города
        return 2;
    }
    if (
        !address.street &&
        (!isHouseCity(address) || !address.house) &&
        !address.stead
    ) {
        // нет улицы
        return 3;
    }
    if (!address.house && !address.stead) {
        // укажите дом
        return 4;
    }
    if (address.fias_level === "8" || (address.qc_geo === "0" || address.qc_geo === "1")) {
        // дом есть в ФИАС
        // или дома нет в ФИАС, но есть на картах
        // смотрим на квартиру
        return address.flat === null ? 5 : 0;
    }
    if (["9", "75"].includes(address.fias_level)) {
        // квартира есть в ФИАС
        // либо это земельный участок
        return 0;
    }
    // неизвестный адрес
    return 10;
}

// город, в котором дома могут быть напрямую подчинены городу,
// минуя улицы
function isHouseCity(address) {
    return address.kladr_id?.startsWith("7700000200000");
}

function isMetro(address) {
    return address.street_type_full === "метро";
}

function qcGeoName(qc_geo) {
    return QC_GEO[qc_geo] || qc_geo;
}

function qcSendName(qc_complete) {
    return QC_SEND[qc_complete] || "Не годится для рассылки: адрес неполный";
}

const self = {

    area(address) {
        return address.area_with_type || "";
    },

    areaWithSubarea(address) {
        const area = utils.join([address.area_with_type, address.sub_area_with_type], ", ");
        return area || "";
    },

    cadNum(address) {
        return address.flat_cadnum || address.house_cadnum || address.stead_cadnum || "";
    },

    capitalMarker(address) {
        if (
            !(address.capital_marker === "0" || address.capital_marker === "4") &&
            (address.city_with_type || address.settlement_with_type)
        ) {
            return capitalMarkerName(address.capital_marker);
        }
        return "";
    },

    centralArea(address) {
        if (address.capital_marker === "4" && address.region_with_type) {
            return capitalMarkerName(address.capital_marker);
        }
    },

    cityAdditional(address) {
        let district = "";
        if (address.city_district && address.street_with_type) {
            district = utils.join([address.city_district_type, address.city_district], " ");
            if (address.city_area) {
                district += `,<br>${address.city_area} адм. округ`;
            }
        }
        return district;
    },

    cityDistrict(address) {
        let district = "";
        if (address.city_district_with_type) {
            district = utils.join([address.city_district_type, address.city_district], " ");
        }
        return district;
    },

    cityWithSettlement(address) {
        if (!address.city && !address.settlement) return null;
        const city_with_type = utils.join([address.city_type, address.city], " ");
        let settlement = "";
        if (address.settlement !== address.city) {
            settlement = address.settlement_with_type;
        }
        return utils.join([city_with_type, settlement], " / ");
    },

    entrance(address) {
        if (!address.entrance) return null;
        return `${address.entrance}-й подъезд`;
    },

    fiasActualityStateName,

    fiasCodes(address) {
        if (!address.fias_id) {
            return "";
        }
        let codes = address.fias_id;

        const act_state = fiasActualityStateName(address.fias_actuality_state);
        if (act_state) {
            codes += ` (${act_state})`;
        }

        return codes;
    },

    fiasLevel(address) {
        return `${address.fias_level} (${fiasLevelName(address.fias_level)})`;
    },

    flatPrice(address) {
        if (!address.flat_price) {
            return "";
        }
        return utils.formatMoney(address.flat_price);
    },

    floor(address) {
        if (!address.floor) return;
        return `${address.floor}-й этаж`;
    },

    hasHistoryValues(address, level) {
        if (!address.history_values) {
            return false;
        }
        // есть историческое значение для улицы
        if (level === "street" && address.street) {
            return true;
        }
        // есть историческое значение для города или нп
        // и в адресе не заполнены следующие компоненты
        if (level === "city" && (address.city || address.settlement)) {
            return !address.street;
        }
        // есть историческое значение области
        // и в адресе не заполнены следующие компоненты
        if (level === "area" && address.area) {
            return !address.city && !address.settlement && !address.street;
        }
        // есть историческое значение для региона
        // и в адресе не заполнены следующие компоненты
        if (level === "region" && address.region) {
            return !address.area && !address.city && !address.settlement && !address.street;
        }
        return false;
    },

    inferQcComplete,

    isGood(address) {
        const {
            qc, qc_complete, qc_geo,
        } = address;
        if (qc === null) {
            const inferedQcComplete = inferQcComplete(address);
            return [2, 3, 4, 5].includes(inferedQcComplete);
        }
        return (
            qc === 0 &&
            qc_complete !== undefined &&
            (qc_complete === 0 || qc_complete === 5 || qc_geo === 0)
        );
    },

    isForeign(address) {
        return (
            address.fias_level === "-1" ||
            (address.hasOwnProperty("qc_complete") && address.qc_complete === 7)
        );
    },

    isValidFiasLevel(address) {
        return address.fias_level && address.fias_level !== "-1";
    },

    isoCodes(address) {
        if (!address.country_iso_code) {
            return "";
        }
        if (address.region_iso_code) {
            return `${address.country_iso_code} / ${address.region_iso_code}`;
        }
        return address.country_iso_code;
    },

    isRegionCity(address) {
        return !address.city && !address.settlement && address.region_type_full === "город";
    },

    geoLink(address) {
        return utils.join(
            ["http://maps.yandex.ru/?text=", address.geo_lat, ",", address.geo_lon],
            "",
        );
    },

    geoLinkDescription(address) {
        let description = `(${qcGeoName(address.qc_geo)})`;
        const qc_geo = Number.parseInt(address.qc_geo, 10);
        if (address.beltway_hit && qc_geo <= 4) {
            description += `,\n ${beltwayName(address.beltway_hit, address.beltway_distance)}`;
        }
        return description;
    },

    geoLinkLabel(address) {
        return `${address.geo_lat}, ${address.geo_lon}`;
    },

    houseWithBlock(address) {
        return utils.join(
            [address.house_type, address.house, address.block_type, address.block],
            " ",
        );
    },

    metro(address, separator = "") {
        if (isMetro(address)) {
            // closest metro to the metro itself makes no sense
            return null;
        }
        return metro.descriptionForMultiple(
            address.city || address.region,
            address.metro,
            separator,
        );
    },

    qcGeoName,
    qcSendName,

    region(address) {
        return address.region_with_type;
    },

    regionWithArea(address) {
        let region = utils.join([address.region_with_type, address.area_with_type], " ");
        if (!region) {
            // eslint-disable-next-line prefer-destructuring
            region = address.region;
        }
        return region;
    },

    validationMessage(address) {
        const qc_complete = inferQcComplete(address);
        return QC_COMPLETE[qc_complete];
    },
};

export default self;
